// pages/DriverScreen.js
// Driver execution screen — LoadDash Services
// Role-gated: admin. Uses tab-based routing via App.modular.js.
// Original IP — LoadDash Services LLC

import React, { useState, useEffect } from "react";
import { NAVY, NAVY2, BLUE, GREEN, RED, AMBER, STEEL, MUTED, BORDER, TEXT2, BG, card, BRAND_NAME, ZONES, PICKUP_SLOTS, ADMIN_PHONE, COMPANY_MARGIN_PCT } from "../constants";
import { LOAD_TIERS } from "../constants";
import { supabase, completeJob, uploadPhoto } from "../services/supabaseService";
import { capturePhoto, CAPTURE_ERRORS } from "../services/captureOrchestrator";
import { trackEvent, EVENTS } from "../analytics/eventTracker";
import { sendSMSNotification } from "../services/notificationService";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import DriverMapScreen from "./DriverMapScreen";
import { DISPLAY_TIER_LEU_MAP, jobLEU, fmtLEU } from "../utils/leuUtils";
import { calcSplit, buildCostSnapshot, MIN_DRIVER_PROFIT_BUFFER } from "../utils/marketConfig";
import { normalizeVehicleProfile, isJobAllowedForTruck, getTruckCapacityPolicy } from "../dispatch/truckCapacityPolicy";

// ── Visual System Tokens ─────────────────────────────────────
const DS = {
  H_PAD: 18,
  CARD_RADIUS: 14,
  STAT_RADIUS: 12,
  PROFIT_BAR_H: 5,
  LABEL: 9,
  MICRO: 9,
  BODY_SM: 11,
  TITLE: 22,
  NAVY_DEEP: "#060e1a",
  NAVY_MID: "#0d1b2e",
  NAVY_LT: "#152238",
  BLUE_LT: "#42a5f5",
  BORDER_DARK: "#1e3558",
  SLATE: "#607d9f",
};

const C = {
  background: "#fff",
  borderRadius: DS.CARD_RADIUS,
  border: `1px solid ${BORDER}`,
  padding: 13,
  marginBottom: 0,
  boxShadow: "0 1px 4px rgba(10,22,40,.03)",
};

const SORA = `@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@500;600;700&display=swap');`;

// ── Business Constants ───────────────────────────────────────
const DEFAULT_MPG = 19;
const DEFAULT_GAS_PRICE = 3.44;
const TIER_OPTIONS = [
  { key: "Small",  label: "Small",  leu: 1,   price: 75  },
  { key: "Half",   label: "Medium", leu: 2,   price: 120 },
  { key: "ThreeQ", label: "Large",  leu: 3,   price: 175 },
  { key: "Full",   label: "PCS Cleanout", leu: 6, price: 295 },
];

const ROUTE_CAPACITY_LEU = 24;
const TRUCK_MAX_LEU_DISPLAY = 3;
function fmtMoney(v) { return "$" + (v != null ? v.toFixed(2) : "0.00"); }

// Payout math is in src/utils/marketConfig.js (shared with DriverMapScreen).
// Market-aware: calcSplit(gross, marketId, zoneKey), buildCostSnapshot(marketId, zoneKey).
const FALLBACK_MARKET = "ut-hill-afb";

function jobMarket(job) { return job?.market_id || FALLBACK_MARKET; }

function profitClass(gross, marketId, zoneKey) {
  const { driverPay } = calcSplit(gross, marketId, zoneKey);
  if (driverPay >= MIN_DRIVER_PROFIT_BUFFER) return "profitable";
  if (driverPay > 0) return "marginal";
  return "loss";
}

function tierLabel(key) {
  if (key === "Half") return "Medium";
  if (key === "ThreeQ") return "Large";
  const t = LOAD_TIERS.find(t => t.key === key);
  return t ? t.label.replace(" Load", "") : key || "Unknown";
}

function tierPrice(key) {
  return TIER_OPTIONS.find(t => t.key === key)?.price || 0;
}

function navToAddr(addr) {
  const ua = navigator.userAgent;
  const url = (/iPhone|iPad|iPod/.test(ua))
    ? "maps://maps.apple.com/?daddr=" + encodeURIComponent(addr)
    : "https://maps.google.com/?daddr=" + encodeURIComponent(addr);
  window.open(url, "_blank");
}

// ── Load size display (replaces LEU in driver-facing UI) ────────────────
const LOAD_FILL = { Small: { pct: 20, color: "#3D90D9", label: "Light load" }, Half: { pct: 45, color: "#3D90D9", label: "Medium load" }, ThreeQ: { pct: 72, color: "#F29727", label: "Large load" }, Full: { pct: 100, color: "#1D9E75", label: "Full truck" } };
function loadInfo(sizeKey) { return LOAD_FILL[sizeKey] || LOAD_FILL.Small; }
function LoadBar({ sizeKey }) {
  const li = loadInfo(sizeKey);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
      <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#1e3050", overflow: "hidden" }}>
        <div style={{ width: `${li.pct}%`, height: "100%", borderRadius: 4, background: li.color }} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 600, color: li.color, whiteSpace: "nowrap" }}>{li.label}</span>
    </div>
  );
}

function ProfitBadge({ cls }) {
  const m = { profitable: { bg: "#e8f5e9", color: GREEN, label: "Good Pay" }, marginal: { bg: "#fff8e1", color: AMBER, label: "Okay Pay" }, loss: { bg: "#fce4e4", color: RED, label: "Low Pay" } };
  const s = m[cls] || m.marginal;
  return <span style={{ background: s.bg, color: s.color, fontSize: DS.MICRO, fontWeight: 700, padding: "3px 9px", borderRadius: 20 }}>{s.label}</span>;
}

// ═══════════════════════════════════════════════════════════════
export default function DriverScreen({ session, role, driverProfile }) {
  const vehicleProfile = normalizeVehicleProfile(driverProfile);
  const truckPolicy = getTruckCapacityPolicy(vehicleProfile);

  const [driverView, setDriverView] = useState("route"); // "route" | "map"
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // "list" | "detail" | "summary"
  const [activeJob, setActiveJob] = useState(null);
  const [selTier, setSelTier] = useState(null);
  const [stagingVal, setStagingVal] = useState("");
  const [flags, setFlags] = useState([]);
  const [bagCount, setBagCount] = useState("");
  const [compPhoto, setCompPhoto] = useState(null);
  const [compPhotoPreview, setCompPhotoPreview] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [toast, setToast] = useState("");
  const [captureErr, setCaptureErr] = useState(null);
  const [doneResult, setDoneResult] = useState(null);
  const online = useOnlineStatus();

  // getUserMedia modal state
  const [modalStream, setModalStream] = useState(null);
  const modalResolveRef = React.useRef(null);
  const modalVideoRef = React.useRef(null);

  const userId = session?.user?.id;
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  // ── Data loading ─────────────────────────────────────────
  useEffect(() => { loadJobs(); }, []);

  async function loadJobs() {
    setLoading(true);
    try {
      let query = supabase
        .from("requests")
        .select("*, payments(*)")
        .in("status", ["pending", "scheduled", "en_route", "in_progress", "complete"])
        .order("created_at", { ascending: true });

      // Driver users only see their own assigned jobs
      if (role === "driver" && userId) {
        query = query.eq("assigned_driver", userId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setJobs(data || []);
    } catch (e) { showToast("Could not load jobs: " + e.message); }
    setLoading(false);
  }

  // ── Job actions ──────────────────────────────────────────
  function openJob(job) {
    setActiveJob(job);
    setSelTier(null);
    setStagingVal(job.placement_location || job.notes || "");
    setFlags([]);
    setBagCount("");
    setCompPhoto(null); setCompPhotoPreview(null);
    setCaptureErr(null);
    setDoneResult(null);
    setView("detail");
    trackEvent(EVENTS.JOB_OPENED, userId, { request_id: job.id, zone: job.zone, size: job.size });
  }

  function toggleFlag(f) {
    const willAdd = !flags.includes(f);
    setFlags(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
    trackEvent(EVENTS.FLAG_TOGGLED, userId, { request_id: activeJob?.id, flag: f, action: willAdd ? "added" : "removed" });
  }

  // ── Camera modal ─────────────────────────────────────────
  function openModal({ stream, onCapture, onCancel }) {
    setModalStream(stream);
    modalResolveRef.current = { onCapture, onCancel };
  }
  function handleModalShutter() {
    const video = modalVideoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob && modalResolveRef.current) modalResolveRef.current.onCapture(blob);
      setModalStream(null); modalResolveRef.current = null;
    }, "image/jpeg", 0.85);
  }
  function handleModalCancel() {
    if (modalResolveRef.current) modalResolveRef.current.onCancel();
    setModalStream(null); modalResolveRef.current = null;
  }

  async function handleCapturePhoto() {
    setCaptureErr(null);
    const result = await capturePhoto(1, { showMediaModal: openModal });
    if (!result.ok) { setCaptureErr(CAPTURE_ERRORS[result.errorCode] || "Camera error"); showToast(CAPTURE_ERRORS[result.errorCode] || "Camera error"); return; }
    setCompPhoto(result.file);
    setCompPhotoPreview(result.previewUrl);
  }

  // ── Completion ───────────────────────────────────────────
  async function handleCompleteJob() {
    if (!selTier || !activeJob) return;
    // Acceptance gate — hard safety backstop (Part 3)
    if (vehicleProfile) {
      const eligibility = isJobAllowedForTruck(vehicleProfile, { ...activeJob, size: selTier });
      if (!eligibility.allowed) {
        showToast("This job exceeds your truck capacity. Contact dispatch.");
        console.error("[DISPATCH] Acceptance blocked:", { jobId: activeJob.id, tier: selTier, reasons: eligibility.reasons });
        return;
      }
    }
    if (!stagingVal.trim()) { showToast("Enter staging location before finishing the job."); return; }
    if (!compPhoto) { showToast("Take a completion photo before finishing the job."); return; }
    setCompleting(true);
    try {
      const photoUrl = await uploadPhoto(compPhoto, userId);

      const tier = TIER_OPTIONS.find(t => t.key === selTier);
      // Canonical system-assigned LEU waterfall
      const baseLEU = jobLEU(activeJob) || 0;
      const verifiedLEU = DISPLAY_TIER_LEU_MAP[selTier];
      const varianceLEU = verifiedLEU - baseLEU;
      const bookedTier = activeJob.size;
      const isBagJob = activeJob.type && activeJob.type.toLowerCase().includes("bagged trash");
      const submittedFlags = isBagJob && bagCount ? [...flags, `bag_count:${bagCount}`] : flags;
      const mkt = jobMarket(activeJob);
      const completionSplit = calcSplit(tier.price, mkt, activeJob.zone);
      const costSnapshot = buildCostSnapshot(mkt, activeJob.zone);

      await completeJob({
        requestId: activeJob.id,
        providerVerifiedLeu: verifiedLEU,
        confirmedStaging: stagingVal,
        flags: submittedFlags,
        systemAssignedLeu: baseLEU,
        systemAssignedTier: bookedTier,
        driverVerifiedTier: selTier,
        completionPhotoUrl: photoUrl,
        completedBy: userId,
        predictedLeuSnapshot: activeJob.predicted_leu ?? null,
        companyMarginAmt: completionSplit.companyMargin,
        driverPayAmt: completionSplit.driverPay,
        variableCostAmt: completionSplit.variableCosts,
        marketId: mkt,
        zoneCostSnapshot: costSnapshot,
      });

      if (flags.length > 0) {
        trackEvent(EVENTS.MISREP_FLAGGED, userId, { request_id: activeJob.id, flags, verified_tier: selTier, booked_tier: bookedTier });
        sendSMSNotification("misrep_alert", ADMIN_PHONE, {
          address: activeJob.addr, size: tierLabel(bookedTier),
          verified_size: tierLabel(selTier), flags: flags.join(", "),
          request_id: activeJob.id,
        }).catch(() => {});
      }
      trackEvent(EVENTS.JOB_VERIFIED, userId, { request_id: activeJob.id, verified_tier: selTier, booked_tier: bookedTier, variance_leu: varianceLEU });

      const tierMatch = selTier === bookedTier;
      setDoneResult({
        job: activeJob,
        aiSaid: tierLabel(bookedTier),
        found: tierLabel(selTier),
        grossPay: tier.price,
        split: completionSplit,
        predictedLEU: baseLEU,
        verifiedLEU,
        varianceLEU,
        tierMatch,
        varianceDir: tierMatch ? "match" : verifiedLEU > baseLEU ? "underreported" : "overreported",
      });
      setView("summary");
      await loadJobs();
    } catch (e) {
      if (e.message && e.message.includes("SESSION_EXPIRED")) {
        showToast("Your session expired. Please sign out and sign back in to complete this job.");
      } else {
        showToast("Error: " + e.message);
      }
    }
    setCompleting(false);
  }

  // ── Derived data ─────────────────────────────────────────
  // Filter jobs by truck capacity eligibility (feed enforcement — Part 2)
  const eligibleJobs = vehicleProfile
    ? jobs.filter(j => j.status === "complete" || isJobAllowedForTruck(vehicleProfile, j).allowed)
    : jobs; // Admin view or missing profile — show all
  const pending = eligibleJobs.filter(j => ["pending", "scheduled", "en_route", "in_progress"].includes(j.status));
  const completed = eligibleJobs.filter(j => j.status === "complete");
  const pendingEarnings = pending.reduce((a, j) => a + calcSplit(tierPrice(j.size), jobMarket(j), j.zone).driverPay, 0);
  const completedEarnings = completed.reduce((a, j) => a + calcSplit(tierPrice(j.size), jobMarket(j), j.zone).driverPay, 0);

  // ── Route LEU metrics ──────────────────────────────────────
  const routeTotalLEU = jobs.reduce((a, j) => a + (jobLEU(j) || 0), 0);
  const routeLoadPct = Math.min(100, Math.round((routeTotalLEU / ROUTE_CAPACITY_LEU) * 100));
  const routeCapColor = routeLoadPct >= 90 ? RED : routeLoadPct >= 70 ? AMBER : GREEN;
  const routeTotalNet = jobs.reduce((a, j) => a + calcSplit(tierPrice(j.size), jobMarket(j), j.zone).driverPay, 0);
  const routeNetPerLEU = routeTotalLEU > 0 ? routeTotalNet / routeTotalLEU : 0;
  const totalFuelEst = pending.reduce((a, j) => a + calcSplit(tierPrice(j.size), jobMarket(j), j.zone).fuelEst, 0);

  // ── Live route labels ──────────────────────────────────────
  const routeDay = (() => {
    const dates = pending.map(j => j.date).filter(Boolean);
    if (dates.length === 0) return new Date().toLocaleDateString("en-US", { weekday: "long" });
    const d = new Date(dates[0] + "T12:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long" });
  })();
  const routeZone = (() => {
    const zones = pending.map(j => j.zone).filter(Boolean);
    if (zones.length === 0) return "—";
    const counts = {};
    zones.forEach(z => { counts[z] = (counts[z] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    if (sorted.length === 1) return `Zone ${sorted[0][0]}`;
    return "Multi-Zone";
  })();
  const routeWindow = (() => {
    const slot = PICKUP_SLOTS.find(s => s.day === routeDay);
    return slot ? slot.time : "—";
  })();

  // ═══ MAP VIEW (driver-local) ═════════════════════════════
  if (driverView === "map") return (
    <DriverMapScreen session={session} role={role} onBackToRoute={() => setDriverView("route")} />
  );

  // ═══ LIST VIEW ═══════════════════════════════════════════
  if (view === "list") return (
    <div style={{ paddingBottom: 80, background: BG, minHeight: "100vh", fontFamily: "'Sora',system-ui,sans-serif" }}>
      <style>{SORA}</style>
      <div style={{ background: `linear-gradient(160deg, ${DS.NAVY_DEEP}, ${DS.NAVY_MID})`, padding: `20px ${DS.H_PAD}px 16px` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: DS.LABEL, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: DS.BLUE_LT }}>{BRAND_NAME} · Driver</div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: online ? GREEN : RED }} />
            <span style={{ fontSize: DS.MICRO, fontWeight: 700, color: "#4a7aaa", letterSpacing: 1, textTransform: "uppercase" }}>{online ? "Online" : "Offline"}</span>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
          <h2 style={{ fontWeight: 900, fontSize: DS.TITLE, color: "#fff", margin: 0, lineHeight: 1.1, letterSpacing: -0.4 }}>{routeDay} Route</h2>
          <button onClick={() => setDriverView("map")} style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${DS.BORDER_DARK}`, background: "rgba(255,255,255,.06)", color: DS.BLUE_LT, fontWeight: 700, fontSize: DS.BODY_SM, cursor: "pointer", fontFamily: "inherit" }}>Map</button>
        </div>
        <div style={{ fontSize: DS.BODY_SM, color: "rgba(255,255,255,.35)", marginBottom: 10 }}>{routeZone} · {routeWindow} · {pending.length} stops</div>
      </div>
      {/* Fuel strip */}
      <div style={{ background: DS.NAVY_MID, padding: `8px ${DS.H_PAD}px`, display: "flex", gap: 20, alignItems: "center", borderBottom: `1px solid ${DS.BORDER_DARK}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 7, height: 7, borderRadius: 2, background: AMBER }} /><span style={{ fontSize: DS.BODY_SM, fontWeight: 600, color: DS.SLATE, fontFamily: "'JetBrains Mono',monospace" }}>{DEFAULT_MPG} MPG</span></div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 7, height: 7, borderRadius: 2, background: DS.BLUE_LT }} /><span style={{ fontSize: DS.BODY_SM, fontWeight: 600, color: DS.SLATE, fontFamily: "'JetBrains Mono',monospace" }}>${DEFAULT_GAS_PRICE} / gal</span></div>
        <div style={{ marginLeft: "auto" }}><span style={{ fontSize: 10, fontWeight: 700, color: DS.BLUE_LT }}>Edit</span></div>
      </div>
      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, margin: "10px 12px 0" }}>
        {[[pending.length, "JOBS", "#0a1628"], [pending.length > 0 ? `$${pendingEarnings.toFixed(2)}` : `$${completedEarnings.toFixed(2)}`, pending.length > 0 ? "NET EST." : "EARNED", GREEN], [`$${totalFuelEst.toFixed(2)}`, "FUEL EST.", RED]].map(([v, l, c]) => (
          <div key={l} style={{ background: "#fff", borderRadius: DS.STAT_RADIUS, border: `1px solid ${BORDER}`, padding: "11px 8px", textAlign: "center" }}>
            <div style={{ fontWeight: 900, fontSize: 20, color: c, lineHeight: 1.1 }}>{v}</div>
            <div style={{ fontSize: DS.MICRO, color: DS.SLATE, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Route Capacity Card */}
      {!loading && jobs.length > 0 && (
        <div style={{ margin: "8px 12px 0" }}>
          <div style={{ background: `linear-gradient(135deg, ${DS.NAVY_MID}, ${DS.NAVY_LT})`, borderRadius: 12, padding: "12px 14px", border: `1px solid ${DS.BORDER_DARK}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: DS.LABEL, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: DS.BLUE_LT }}>Route Load</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#fff", fontFamily: "'JetBrains Mono',monospace" }}>{routeLoadPct}%</div>
            </div>
            <div style={{ height: 8, background: DS.BORDER_DARK, borderRadius: 4, overflow: "hidden", marginBottom: 6 }}>
              <div style={{ height: "100%", borderRadius: 4, width: `${routeLoadPct}%`, background: `linear-gradient(90deg, ${DS.BLUE_LT}, ${GREEN})` }} />
            </div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,.35)" }}>{pending.length} job{pending.length !== 1 ? "s" : ""} on route · {100 - routeLoadPct}% capacity remaining</div>
          </div>
        </div>
      )}

      <div style={{ padding: "10px 12px 0" }}>
        {loading ? <div style={{ textAlign: "center", padding: 40, color: STEEL }}>Loading...</div> : jobs.length === 0 && role === "driver" ? (
          <div style={{ ...C, textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: NAVY, marginBottom: 6 }}>No jobs assigned yet</div>
            <div style={{ fontSize: 12, color: STEEL }}>Check back later or contact dispatch</div>
          </div>
        ) : (
          <>
            {pending.length > 0 && (
              <>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: STEEL, textTransform: "uppercase", marginBottom: 10 }}>Pending</div>
                {pending.map((job, i) => {
                  const price = tierPrice(job.size);
                  const split = calcSplit(price, jobMarket(job), job.zone);
                  const pc = profitClass(price, jobMarket(job), job.zone);
                  const hasMismatch = job.tier_override_direction === "down";
                  const barRatio = price > 0 ? Math.max(0, Math.min(1, split.driverPay / price)) : 0;
                  return (
                    <div key={job.id} onClick={() => openJob(job)} style={{ ...C, cursor: "pointer", borderLeft: `3px solid ${AMBER}`, margin: "8px 0 0", padding: 13 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                          <div style={{ width: 24, height: 24, borderRadius: "50%", background: BLUE, color: "#fff", fontWeight: 800, fontSize: DS.BODY_SM, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: NAVY, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{job.addr || "—"}</div>
                            <div style={{ fontSize: DS.BODY_SM, color: STEEL }}>{tierLabel(job.size)} · ${price} gross · {loadInfo(job.size).label}</div>
                          </div>
                        </div>
                        <button onClick={e => { e.stopPropagation(); navToAddr(job.addr); }} style={{ padding: "6px 11px", borderRadius: 8, border: "none", background: BLUE, color: "#fff", fontWeight: 700, fontSize: DS.BODY_SM, cursor: "pointer", flexShrink: 0, marginLeft: 8 }}>Navigate</button>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: hasMismatch ? AMBER : GREEN }} />
                        <span style={{ fontSize: DS.LABEL, color: STEEL }}>{job.placement_location || "Driveway"}{hasMismatch ? " · Mismatch" : ""}</span>
                      </div>
                      <div style={{ height: DS.PROFIT_BAR_H, background: "#e8eef6", borderRadius: 2, overflow: "hidden", marginBottom: 6 }}>
                        <div style={{ height: "100%", borderRadius: 2, width: `${Math.round(barRatio * 100)}%`, background: pc === "profitable" ? GREEN : pc === "marginal" ? AMBER : RED }} />
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <ProfitBadge cls={pc} />
                        <span style={{ fontSize: DS.BODY_SM, fontWeight: 600, color: STEEL }}>You keep ${split.driverPay.toFixed(2)} · Costs ${split.variableCosts.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
            {completed.length > 0 && (
              <>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: STEEL, textTransform: "uppercase", margin: "14px 0 10px" }}>Completed</div>
                {completed.slice(0, 5).map(job => (
                  <div key={job.id} style={{ ...C, borderLeft: `3px solid ${GREEN}`, opacity: 0.65 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: NAVY }}>{job.addr || "—"}</div>
                    <div style={{ fontSize: DS.LABEL, color: STEEL }}>{tierLabel(job.size)} · ${calcSplit(tierPrice(job.size), jobMarket(job), job.zone).driverPay.toFixed(2)} earned</div>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>

      {/* EOD Card */}
      {!loading && jobs.length > 0 && (
        <div style={{ background: "linear-gradient(135deg,#060e1a,#0d1b2e)", borderRadius: 14, padding: 14, margin: "8px 12px 12px", border: "1px solid #1e3558" }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "#42a5f5", marginBottom: 10 }}>End of Day</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
            <div style={{ background: "rgba(255,255,255,.05)", borderRadius: 10, padding: 11, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#fff" }}>${routeTotalNet.toFixed(2)}</div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,.35)", marginTop: 2 }}>Total Net Est.</div>
            </div>
            <div style={{ background: "rgba(255,255,255,.05)", borderRadius: 10, padding: 11, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#42a5f5" }}>{jobs.length}</div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,.35)", marginTop: 2 }}>Total Jobs</div>
            </div>
          </div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,.2)", textAlign: "center" }}>Gross − {(COMPANY_MARGIN_PCT * 100).toFixed(0)}% company − variable costs</div>
        </div>
      )}

      {toast && <div style={{ position: "fixed", bottom: 80, left: 18, right: 18, background: NAVY, color: "#fff", padding: "12px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 100, textAlign: "center" }}>{toast}</div>}
    </div>
  );

  // ═══ COMPLETION SUMMARY VIEW ═════════════════════════════
  if (view === "summary" && doneResult) {
    const dr = doneResult;
    return (
      <div style={{ paddingBottom: 80, background: BG, minHeight: "100vh", fontFamily: "'Sora',system-ui,sans-serif" }}>
        <style>{SORA}</style>
        <div style={{ background: dr.tierMatch ? "linear-gradient(160deg, #060e1a, #0d2818)" : `linear-gradient(160deg, ${DS.NAVY_DEEP}, ${DS.NAVY_MID})`, padding: "20px 18px 16px", textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: dr.tierMatch ? "#e0f2f1" : "#fff8e1", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px", fontSize: 22, color: dr.tierMatch ? GREEN : AMBER, fontWeight: 900 }}>{dr.tierMatch ? "✓" : "!"}</div>
          <div style={{ fontWeight: 900, fontSize: 18, color: dr.tierMatch ? GREEN : "#fff" }}>Job Complete</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.35)", marginTop: 4 }}>{dr.job?.addr || "—"} · Saved</div>
        </div>
        <div style={{ padding: "12px 12px" }}>
          {/* Match/mismatch */}
          <div style={{ ...C, border: `2px solid ${dr.tierMatch ? GREEN : AMBER}`, margin: "0 0 8px", padding: 14 }}>
            <div style={{ textAlign: "center", marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: dr.tierMatch ? GREEN : AMBER }}>{dr.tierMatch ? "Match" : "Changed"}</div>
            </div>
            <div style={{ height: 1, background: BORDER, marginBottom: 12 }} />
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: STEEL, textTransform: "uppercase", marginBottom: 10 }}>Estimate vs Actual</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 12 }}>
              {[[dr.aiSaid, "We Said", NAVY], [dr.found, "You Found", BLUE], [`$${dr.split.driverPay.toFixed(2)}`, "You Keep", GREEN]].map(([v, l, c]) => (
                <div key={l} style={{ textAlign: "center", background: BG, borderRadius: 8, padding: 10 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: c }}>{v}</div>
                  <div style={{ fontSize: 9, color: STEEL, fontWeight: 600, marginTop: 2 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Load comparison */}
          <div style={{ ...C, margin: "8px 0 0", padding: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: STEEL, textTransform: "uppercase", marginBottom: 10 }}>Load Comparison</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              <div style={{ textAlign: "center", background: BG, borderRadius: 8, padding: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: NAVY }}>{dr.aiSaid}</div>
                <div style={{ fontSize: 9, color: "#8fa5be" }}>Expected</div>
              </div>
              <div style={{ textAlign: "center", background: BG, borderRadius: 8, padding: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: BLUE }}>{dr.found}</div>
                <div style={{ fontSize: 9, color: "#8fa5be" }}>Verified</div>
              </div>
            </div>
            {(() => {
              const abs = Math.abs(dr.varianceLEU);
              const color = abs === 0 ? GREEN : abs <= 2 ? AMBER : RED;
              const bg = abs === 0 ? "#e8f5e9" : abs <= 2 ? "#fff8e1" : "#fce4e4";
              const label = abs === 0 ? "Accurate" : abs <= 2 ? "Slight difference" : "Large difference";
              return <div style={{ marginTop: 8, textAlign: "center", padding: "5px 10px", borderRadius: 6, background: bg, fontSize: 11, fontWeight: 700, color }}>{label}</div>;
            })()}
          </div>

          {/* Earnings */}
          <div style={{ ...C, margin: "8px 0 0", padding: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: STEEL, textTransform: "uppercase", marginBottom: 10 }}>Earnings</div>
            {[
              ["Gross", `$${dr.split.gross.toFixed(2)}`, NAVY],
              [`Platform fee (${(COMPANY_MARGIN_PCT * 100).toFixed(0)}%)`, `-$${dr.split.companyMargin.toFixed(2)}`, STEEL],
              ["Stripe processing", `-$${dr.split.stripeFee.toFixed(2)}`, STEEL],
              ["Dump fee — Davis County Landfill", `-$${dr.split.dumpFee.toFixed(2)}`, STEEL],
              ["Fuel est.", `-$${dr.split.fuelEst.toFixed(2)}`, STEEL],
              ["Your Pay", `$${dr.split.driverPay.toFixed(2)}`, GREEN],
            ].map(([l, v, c]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${BORDER}` }}>
                <span style={{ fontSize: 12, color: STEEL }}>{l}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: c }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Route Impact */}
          <div style={{ ...C, margin: "8px 0 0", padding: 14 }}>
            <div style={{ fontSize: DS.LABEL, fontWeight: 700, letterSpacing: 2, color: STEEL, textTransform: "uppercase", marginBottom: 8 }}>Route Update</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              <div style={{ textAlign: "center", background: BG, borderRadius: 8, padding: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: BLUE }}>{dr.found}</div>
                <div style={{ fontSize: DS.MICRO, color: "#8fa5be" }}>This Job</div>
              </div>
              <div style={{ textAlign: "center", background: BG, borderRadius: 8, padding: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: GREEN }}>{routeLoadPct}%</div>
                <div style={{ fontSize: DS.MICRO, color: STEEL }}>Route Filled</div>
              </div>
            </div>
          </div>

          <button onClick={() => { setView("list"); setDoneResult(null); setActiveJob(null); }} style={{ width: "100%", padding: 14, borderRadius: 12, border: `1.5px solid ${BORDER}`, background: "#fff", color: NAVY, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Back to Route</button>
        </div>
        {toast && <div style={{ position: "fixed", bottom: 80, left: 18, right: 18, background: NAVY, color: "#fff", padding: "12px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 100, textAlign: "center" }}>{toast}</div>}
      </div>
    );
  }

  // ═══ DETAIL VIEW ═════════════════════════════════════════
  const job = activeJob;
  if (!job) return null;
  const bookedPrice = tierPrice(job.size);
  const detailSplit = calcSplit(bookedPrice, jobMarket(job), job.zone);
  const pc = profitClass(bookedPrice, jobMarket(job), job.zone);
  const hasMismatch = job.tier_override_direction === "down";
  const isBagJob = job.type && job.type.toLowerCase().includes("bagged trash");
  const stagingValid = stagingVal.trim().length > 0;
  const canComplete = !!selTier && !!compPhoto && stagingValid && !completing;

  return (
    <div style={{ paddingBottom: 80, background: BG, minHeight: "100vh", fontFamily: "'Sora',system-ui,sans-serif" }}>
      <style>{SORA}</style>
      {/* Header */}
      <div style={{ background: `linear-gradient(160deg, ${DS.NAVY_DEEP}, ${DS.NAVY_MID})`, padding: "20px 18px 16px" }}>
        <button onClick={() => setView("list")} style={{ background: "none", border: "none", color: DS.BLUE_LT, fontWeight: 700, fontSize: 12, cursor: "pointer", padding: 0, marginBottom: 10, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>← Back to Route</button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, color: "#fff", marginBottom: 2 }}>{job.addr || "—"}</div>
            <div style={{ fontSize: DS.BODY_SM, color: "rgba(255,255,255,.35)" }}>{tierLabel(job.size)}{job.zone ? ` · Zone ${job.zone}` : ""} · {loadInfo(job.size).label}</div>
          </div>
          <button onClick={() => navToAddr(job.addr)} style={{ padding: "9px 14px", borderRadius: 8, border: "none", background: BLUE, color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Nav</button>
        </div>
      </div>

      <div style={{ padding: "12px 12px" }}>
        {/* Economics */}
        <div style={{ ...C, border: detailSplit.driverPay < 0 ? `1.5px solid ${RED}` : `1px solid ${BORDER}`, padding: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: STEEL, textTransform: "uppercase", marginBottom: 10 }}>Economics</div>
          {[
            ["Gross", `$${detailSplit.gross.toFixed(2)}`, NAVY],
            [`Platform fee (${(COMPANY_MARGIN_PCT * 100).toFixed(0)}%)`, `-$${detailSplit.companyMargin.toFixed(2)}`, STEEL],
            ["Stripe processing", `-$${detailSplit.stripeFee.toFixed(2)}`, STEEL],
            ["Dump fee — Davis County Landfill", `-$${detailSplit.dumpFee.toFixed(2)}`, STEEL],
            ["Fuel est.", `-$${detailSplit.fuelEst.toFixed(2)}`, STEEL],
            ["Your Pay", `$${detailSplit.driverPay.toFixed(2)}`, detailSplit.driverPay >= 0 ? GREEN : RED],
          ].map(([l, v, c]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${BORDER}` }}>
              <span style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>{l}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: c }}>{v}</span>
            </div>
          ))}
          <div style={{ marginTop: 8 }}><ProfitBadge cls={pc} /></div>
          {detailSplit.driverPay < 0 && <div style={{ fontSize: 11, color: RED, marginTop: 6 }}>Variable costs exceed gross for this job</div>}
        </div>

        {/* Load Check */}
        <div style={C}>
          <div style={{ fontSize: DS.LABEL, fontWeight: 700, letterSpacing: 2, color: BLUE, textTransform: "uppercase", marginBottom: 10 }}>Check Load</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
            <div style={{ background: BG, borderRadius: 8, padding: 8, textAlign: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: NAVY }}>{tierLabel(job.size)}</div>
              <LoadBar sizeKey={job.size} />
              <div style={{ fontSize: DS.MICRO, color: "#8fa5be", marginTop: 4 }}>Expected</div>
            </div>
            <div style={{ background: BG, borderRadius: 8, padding: 8, textAlign: "center" }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: selTier ? BLUE : STEEL }}>{selTier ? tierLabel(selTier) : "—"}</div>
              {selTier ? <LoadBar sizeKey={selTier} /> : <div style={{ height: 8, borderRadius: 4, background: "#1e3050", marginTop: 4 }} />}
              <div style={{ fontSize: DS.MICRO, color: "#8fa5be", marginTop: 4 }}>Your Call</div>
            </div>
          </div>
          {selTier && selTier !== job.size && (
            <div style={{ fontSize: DS.BODY_SM, fontWeight: 600, color: AMBER, textAlign: "center", marginBottom: 4 }}>Load size changed from {tierLabel(job.size)} to {tierLabel(selTier)}</div>
          )}
          <div style={{ fontSize: DS.MICRO, color: "#8fa5be", marginTop: 4 }}>Pick a load size to finish the job.</div>
        </div>

        {/* Job info */}
        <div style={C}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: STEEL, textTransform: "uppercase", marginBottom: 10 }}>Job Info</div>
          {[
            ["Booked Load", tierLabel(job.size)],
            ["Window", job.pickup_window || "—"],
            ["Placement", job.placement_location || "Driveway"],
          ].filter(([, v]) => v && v !== "—").map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${BORDER}` }}>
              <span style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>{l}</span>
              <span style={{ fontSize: 13, color: NAVY, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
          {job.phone && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${BORDER}` }}>
              <span style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>Phone</span>
              <a href={`tel:${job.phone}`} style={{ fontSize: 13, color: BLUE, fontWeight: 700, textDecoration: "none" }}>{job.phone}</a>
            </div>
          )}
          {job.notes && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: STEEL, textTransform: "uppercase", marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 12, color: TEXT2, lineHeight: 1.5 }}>{job.notes}</div>
            </div>
          )}
          <div style={{ marginTop: 10, padding: "8px 10px", background: "#fff8e1", borderRadius: 8, border: "1px solid #fcd34d" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>Cardboard</div>
            <div style={{ fontSize: 11, color: "#78350f", lineHeight: 1.5 }}>Flat and broken down only. Max 15 boxes per load unit. Leave any assembled boxes behind and note in job completion.</div>
          </div>
        </div>

        {hasMismatch && (
          <div style={{ ...C, background: "#fce4e4", border: "1px solid #ef9a9a" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: RED, textTransform: "uppercase", letterSpacing: .5, marginBottom: 4 }}>Mismatch on File</div>
            <div style={{ fontSize: 11, color: "#7f1d1d", lineHeight: 1.5 }}>Customer selected {tierLabel(job.size)}. System assigned a higher tier.</div>
          </div>
        )}

        {/* Customer photos */}
        {(job.photo1_url || job.photo2_url) && (
          <div style={C}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: STEEL, textTransform: "uppercase", marginBottom: 8 }}>Customer Photos</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[job.photo1_url, job.photo2_url].filter(Boolean).map((url, i) => (
                <img key={i} src={url} alt="" style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 8, border: `1px solid ${BORDER}` }} />
              ))}
            </div>
          </div>
        )}

        {/* Verification */}
        <div style={C}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: BLUE, textTransform: "uppercase", marginBottom: 10 }}>What Did You Find?</div>
          <div style={{ background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 11, color: "#0c447c", fontWeight: 600 }}>Record what you actually found on site.</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {TIER_OPTIONS.map(t => (
              <button key={t.key} onClick={() => { setSelTier(t.key); trackEvent(EVENTS.TIER_SELECTED, userId, { request_id: activeJob.id, selected_tier: t.key, booked_tier: activeJob.size }); }} style={{ flex: 1, padding: "11px 4px", borderRadius: 8, border: selTier === t.key ? `2px solid ${BLUE}` : `1.5px solid ${BORDER}`, background: selTier === t.key ? "#e3f2fd" : "#fff", color: selTier === t.key ? BLUE : STEEL, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>{t.label}</button>
            ))}
          </div>

          {/* Staging */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 4 }}>Staging confirmed</div>
            <input value={stagingVal} onChange={e => setStagingVal(e.target.value)} placeholder="Where were items staged?" style={{ width: "100%", background: "#f6f9fc", border: `1.5px solid ${!stagingValid && selTier ? RED : BORDER}`, borderRadius: 8, color: NAVY, fontSize: 12, padding: "9px 11px", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            {!stagingValid && selTier && <div style={{ fontSize: 10, color: RED, marginTop: 4 }}>Staging location is required</div>}
          </div>

          {/* Bag count (bag jobs only) */}
          {isBagJob && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 4 }}>Bag count</div>
              <input type="number" inputMode="numeric" min="0" value={bagCount} onChange={e => setBagCount(e.target.value)} placeholder="How many bags?" style={{ width: "100%", background: "#f6f9fc", border: `1.5px solid ${BORDER}`, borderRadius: 8, color: NAVY, fontSize: 12, padding: "9px 11px", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>
          )}

          {/* Flags */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 6 }}>Flag issues</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {["Load misrepresented", "Not at staging", "Access blocked", "Hazardous"].map(f => (
                <button key={f} onClick={() => toggleFlag(f)} style={{ padding: "6px 10px", borderRadius: 6, border: flags.includes(f) ? `1.5px solid ${RED}` : `1.5px solid ${BORDER}`, background: flags.includes(f) ? "#fce4e4" : "#fff", color: flags.includes(f) ? RED : STEEL, fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{f}</button>
              ))}
            </div>
          </div>

          {/* Completion photo */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 6 }}>Completion photo</div>
            {compPhotoPreview ? (
              <div style={{ position: "relative" }}>
                <img src={compPhotoPreview} alt="" style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 8 }} />
                <button onClick={() => { setCompPhoto(null); setCompPhotoPreview(null); }} style={{ position: "absolute", top: 4, right: 4, background: "rgba(10,22,40,.8)", color: "#fff", border: "none", borderRadius: "50%", width: 22, height: 22, fontSize: 12, cursor: "pointer" }}>x</button>
              </div>
            ) : (
              <button onClick={handleCapturePhoto} style={{ width: "100%", height: 60, borderRadius: 8, border: `1.5px dashed ${BORDER}`, background: "#f6f9fc", color: STEEL, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Capture Completion Photo</button>
            )}
            {captureErr && <div style={{ fontSize: 10, color: RED, marginTop: 4 }}>{captureErr}</div>}
          </div>

          {/* Complete button */}
          <button onClick={handleCompleteJob} disabled={!canComplete} style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: canComplete ? "linear-gradient(135deg,#2d5bbf,#4a8fe8)" : "#e8eef6", color: canComplete ? "#fff" : MUTED, fontWeight: 700, fontSize: 14, cursor: canComplete ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
            {completing ? "Completing..." : !selTier ? "Select load size to continue" : !stagingValid ? "Enter staging location to continue" : !compPhoto ? "Take completion photo to continue" : `Complete — ${TIER_OPTIONS.find(t => t.key === selTier)?.label} · Pay $${calcSplit(TIER_OPTIONS.find(t => t.key === selTier)?.price || 0, jobMarket(job), job.zone).driverPay.toFixed(2)}`}
          </button>
        </div>
      </div>

      {/* Camera modal */}
      {modalStream && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(10,22,40,.9)", zIndex: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", marginBottom: 10 }}>Completion Photo</div>
          <video autoPlay playsInline muted style={{ width: "100%", maxWidth: 480, maxHeight: "60vh", borderRadius: 12, background: "#000" }} ref={el => { modalVideoRef.current = el; if (el && modalStream) el.srcObject = modalStream; }} />
          <div style={{ display: "flex", gap: 16, marginTop: 20 }}>
            <button onClick={handleModalCancel} style={{ padding: "13px 28px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,.3)", background: "transparent", color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button onClick={handleModalShutter} style={{ padding: "13px 36px", borderRadius: 12, border: "none", background: "#fff", color: NAVY, fontWeight: 800, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>Capture</button>
          </div>
        </div>
      )}

      {toast && <div style={{ position: "fixed", bottom: 80, left: 18, right: 18, background: NAVY, color: "#fff", padding: "12px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 100, textAlign: "center" }}>{toast}</div>}
    </div>
  );
}
